import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { SbModal, SbUpload, SbButton, SbNotification } from '../index';

const SbImageCropUpload = ({
  value,
  onChange,
  accept = '.png,.jpg,.jpeg,.svg',
  maxSize = 2, // MB
  aspectRatio = 1,
  uploadText = 'Upload Image',
  uploadHint = 'PNG, JPG, SVG · max 2MB',
  previewSize = { maxHeight: '80px', maxWidth: '80px' },
  cropModalWidth = 600,
  cropAreaHeight = 400,
  renderUploadArea,
  onError,
}) => {
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    
    return new Promise((resolve) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size based on aspect ratio
        if (aspectRatio === 1) {
          const size = Math.min(pixelCrop.width, pixelCrop.height);
          canvas.width = size;
          canvas.height = size;
        } else {
          canvas.width = pixelCrop.width;
          canvas.height = pixelCrop.height;
        }
        
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          canvas.width,
          canvas.height
        );
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      };
    });
  };

  const handleCropConfirm = async () => {
    try {
      const croppedBlob = await createCroppedImage(imageToCrop, croppedAreaPixels);
      
      // Convert blob to file
      const croppedFile = new File([croppedBlob], 'cropped-image.png', { type: 'image/png' });
      
      // Call onChange with the cropped file
      if (onChange) {
        onChange(croppedFile);
      }
      
      // Close crop modal
      setCropModalVisible(false);
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      console.error('Error cropping image:', error);
      const errorMessage = 'Failed to crop the image';
      SbNotification.error('Crop Failed', errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  const handleCropCancel = () => {
    setCropModalVisible(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const beforeUpload = (file) => {
    const acceptedTypes = accept.split(',').map(ext => {
      const mimeTypes = {
        '.png': 'image/png',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.svg': 'image/svg+xml',
      };
      return mimeTypes[ext.trim()];
    });

    const isValidType = acceptedTypes.includes(file.type);
    if (!isValidType) {
      const errorMessage = `Only ${accept} files are allowed`;
      SbNotification.error('Invalid File Type', errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      return false;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      const errorMessage = `File must be smaller than ${maxSize}MB`;
      SbNotification.error('File Too Large', errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      return false;
    }

    // For SVG files, skip cropping
    if (file.type === 'image/svg+xml') {
      if (onChange) {
        onChange(file);
      }
      return false;
    }

    // For other images, open crop modal
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageToCrop(e.target.result);
      setCropModalVisible(true);
    };
    reader.readAsDataURL(file);
    
    return false; // Prevent auto upload
  };

  const getPreviewUrl = () => {
    if (!value) return null;
    
    // If value is a File object
    if (value instanceof File) {
      return URL.createObjectURL(value);
    }
    
    // If value is a URL string
    if (typeof value === 'string') {
      return value;
    }
    
    return null;
  };

  const previewUrl = getPreviewUrl();

  // Default upload area render
  const defaultUploadArea = (
    <div 
      style={{ 
        border: '2px dashed #d1d5db',
        borderRadius: '8px',
        padding: '32px 16px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      {previewUrl ? (
        <div>
          <img 
            src={previewUrl} 
            alt="Preview" 
            style={{ ...previewSize, marginBottom: '12px' }} 
          />
          <p style={{ fontSize: '14px', fontWeight: 500, margin: '8px 0 4px 0' }}>
            Click to change
          </p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
          <p style={{ fontSize: '14px', fontWeight: 500, margin: '8px 0 4px 0' }}>
            {uploadText}
          </p>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            {uploadHint}
          </span>
        </>
      )}
    </div>
  );

  return (
    <>
      <SbUpload
        accept={accept}
        maxCount={1}
        beforeUpload={beforeUpload}
        showUploadList={false}
      >
        {renderUploadArea ? renderUploadArea(previewUrl) : defaultUploadArea}
      </SbUpload>

      {/* Crop Modal */}
      <SbModal
        title={`Crop Image (${aspectRatio === 1 ? '1:1' : `${aspectRatio}:1`} Ratio)`}
        open={cropModalVisible}
        onCancel={handleCropCancel}
        width={cropModalWidth}
        footer={[
          <SbButton key="cancel" buttonType="secondary" onClick={handleCropCancel}>
            Cancel
          </SbButton>,
          <SbButton key="crop" buttonType="primary" onClick={handleCropConfirm}>
            Crop & Save
          </SbButton>
        ]}
      >
        <div style={{ position: 'relative', width: '100%', height: `${cropAreaHeight}px`, background: '#000' }}>
          {imageToCrop && (
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
            Zoom
          </label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: '100%' }}
          />
        </div>
      </SbModal>
    </>
  );
};

export default SbImageCropUpload;
